import { create } from "zustand";

import proxy from "api/proxy";
import { getStorage, setStorage } from "store/local";

const fieldsCats = [
  "category",
  "category.name",
  "category.name_icon",
  "category.childs",
  "category.childs.name",
  "category.childs.name_icon",
];

const modelCats = "sale.shop-product.category";

const orderCats = [["category.name", "ASC"]];

const fieldsPts = [
  "name",
  "list_price",
  "default_uom",
  "sale_uom",
  "sale_price",
  "sale_price_taxed",
  "products_mix.option",
  "products_mix.quantity",
  "products_mix.list_price",
  "products_mix.unit",
  "products_mix.option.name",
  "products_mix.sale_price_taxed",
  "products_mix.option.salable",
];

const modelPts = "product.product";
const orderPts = [["code", "ASC"]];
const limit = 100;

async function getProducts(categoryId, productTagActive, shopId, priceListId) {
  const domain = [
    ["categories", "=", categoryId],
    ["template.salable", "=", true],
    ["template.active", "=", true],
  ];

  if (shopId) {
    domain.push(["analytic_accounts.shop", "=", shopId]);
  }
  const fields = [...fieldsPts];
  if (productTagActive) {
    fields.push("tags.name");
  }
  const session = getStorage("ctxSession");
  setStorage("ctxSession", { ...session, price_list: priceListId });
  const { data } = await proxy.search(
    modelPts,
    domain,
    fields,
    limit,
    orderPts,
  );
  let res = new Map();
  if (data) {
    const list = data.map((rec) => [rec.id, rec]);
    res = new Map(list);
  }
  return res;
}

function collectCategoryIds(categories) {
  const ids = [];
  const stack = [...(categories?.values?.() ?? [])];
  while (stack.length > 0) {
    const cat = stack.pop();
    if (!cat) continue;
    ids.push(cat.id);
    if (cat.childs) {
      stack.push(...cat.childs.values());
    }
  }
  return ids;
}

async function getProductsByQuery(query, productTagActive, categoryIds, priceListId) {
  const domain = [
    ["template.salable", "=", true],
    ["template.active", "=", true],
    [
      "OR",
      ["name", "ilike", `%${query}%`],
      ["code", "ilike", `%${query}%`],
    ],
    ["template.categories", "in", categoryIds],
  ];

  const fields = [...fieldsPts];
  if (productTagActive) {
    fields.push("tags.name");
  }
  const session = getStorage("ctxSession");
  setStorage("ctxSession", { ...session, price_list: priceListId });
  const { data } = await proxy.search(
    modelPts,
    domain,
    fields,
    limit,
    [["name", "ASC"]],
  );
  let res = new Map();
  if (data) {
    const list = data.map((rec) => [rec.id, rec]);
    res = new Map(list);
  }
  return res;
}

async function getCategories(shopId) {
  const domain = [["shop", "=", shopId]];
  const { data, error } = await proxy.search(
    modelCats,
    domain,
    fieldsCats,
    100,
    orderCats,
  );
  const categories = new Map();
  if (!error) {
    for (const ct of data) {
      const cat = ct["category."];
      if (cat["childs."].length > 0) {
        const list = cat["childs."].map((rec) => [rec.id, rec]);
        cat.childs = new Map(list);
      } else {
        delete cat["childs"];
      }
      categories.set(cat.id, cat);
    }
  }
  return { categories, error };
}

async function getTables(shopId) {
  const fields = ["id", "name", "sale", "zone.name", "capacity"];
  const domain = [
    ["shop", "=", shopId],
    // ["state", "=", "available"],
  ];
  const orderTables = [["name", "ASC"]];

  const { data, error } = await proxy.search(
    "sale.shop.table",
    domain,
    fields,
    null,
    orderTables,
  );
  return data;
}

export const useStoreRest = create((set, get) => ({
  shops: {},
  isTagsActive: false,
  filterByShop: null,
  shopAccess: null,
  defaultShop: null,
  agentRequired: false,
  requestCodeSaleEdit: false,
  agents: [],
  initShop: async (shopId) => {
    const shops = get().shops;
    if (shops[shopId]) return;

    const { categories, error } = await getCategories(shopId);
    const tables = await getTables(shopId);
    const { data: shopsData, _ } = await proxy.search(
      "sale.shop",
      [["id", "=", shopId]],
      ["id", "price_list", "local_printer"],
    );

    if (!error) {
      shops[shopId] = {
        categories,
        categoryIds: collectCategoryIds(categories),
        price_list: shopsData[0].price_list,
        local_printer: shopsData[0].local_printer,
        products: {},
        catsIndex: [],
        tables: tables,
        currentCats: categories,
        unavailables: [],
        searchResults: null,
        searchQuery: null,
        searchProductsCache: {},
      };
      set({ shops });
    }
  },
  setIsFilterByShop: async () => {
    const filterByShop = get().filterByShop;
    if (filterByShop !== null) return;

    const result = await proxy.search(
      "sale.configuration",
      [],
      ["filter_product_by_analytic"],
    );
    const agent = await proxy.search(
      "sale.shop",
      [],
      ["agent_pos_required"],
      1,
    );
    const agents = await proxy.search(
      "commission.agent",
      [["code", "!=", null]],
      ["id", "code", "rec_name", "user"],
    );

    const toUpdate = {};

    if (!result.error) {
      toUpdate.filterByShop = result.data[0].filter_product_by_analytic;
    }

    if (!agent.error) {
      toUpdate.agentRequired = agent.data[0].agent_pos_required;
    }

    if (!agents.error) {
      toUpdate.agents = agents.data;
    }

    const { data: fieldCheck } = await proxy.search(
      "ir.model.field",
      [
        ["model.model", "=", "sale.configuration"],
        ["name", "=", "request_code_sale_edit"],
      ],
      ["id"],
    );
    if (fieldCheck?.length > 0) {
      const { data: saleConfig } = await proxy.search(
        "sale.configuration",
        [],
        ["request_code_sale_edit"],
      );
      toUpdate.requestCodeSaleEdit =
        saleConfig?.[0]?.request_code_sale_edit ?? false;
    }

    set(toUpdate);
  },
  gotoCategory: async (shopId, categoryId) => {
    const shops = get().shops;
    const shop = shops[shopId];
    if (!shop) return;

    const { catsIndex, categories, products } = shop;

    if (categoryId) {
      catsIndex.push(categoryId);
    } else {
      catsIndex.pop();
    }

    let current = categories;
    for (const idx of catsIndex) {
      current = current.get(idx);
      current = current.childs ?? categories;
    }

    if (categoryId && !products[categoryId]) {
      const isTagsActive = get().isTagsActive;
      const filterByShop = get().filterByShop;
      const productsOfCategory = await getProducts(
        categoryId,
        isTagsActive,
        filterByShop ? shopId : null,
        shop.price_list,
      );

      products[categoryId] = productsOfCategory;
    }

    set({
      shops: {
        ...shops,
        [shopId]: {
          ...shop,
          products,
          catsIndex,
          currentCats: current,
        },
      },
    });
  },
  searchProducts: async (shopId, query) => {
    const shops = get().shops;
    const shop = shops[shopId];
    if (!shop) return;

    const key = query.trim().toLowerCase();
    if (!key) return;

    const cached = shop.searchProductsCache?.[key];
    if (cached) {
      set({
        shops: {
          ...shops,
          [shopId]: { ...shop, searchResults: cached, searchQuery: key },
        },
      });
      return;
    }

    const isTagsActive = get().isTagsActive;
    const products = await getProductsByQuery(
      key,
      isTagsActive,
      shop.categoryIds,
      shop.price_list,
    );

      set({
        shops: {
          ...shops,
          [shopId]: {
            ...shop,
            searchResults: products,
            searchQuery: key,
            searchProductsCache: {
              ...shop.searchProductsCache,
              [key]: products,
            },
          },
        },
      });
  },
  setProducts: async (shopId, categoryId) => {
    const shops = get().shops;
    const shop = shops[shopId];
    if (!shop) return;

    if (shop.products[categoryId]) return;

    const isTagsActive = get().isTagsActive;
    const productsOfCategory = await getProducts(categoryId, isTagsActive);
    shop.products[categoryId] = productsOfCategory;

    set({ shops: { ...shops, [shopId]: shop } });
  },
  setShopAccess: async (session, shop) => {
    const shopAccess = get().shopAccess;

    const toUpdate = { shopAccess };
    if (!shopAccess) {
      const { data } = await proxy.search(
        "res.user",
        [["id", "=", session.user]],
        ["shops.name"],
      );
      toUpdate.shopAccess = data[0]["shops."];
    }
    toUpdate.defaultShop = shop ? shop : session?.shop;
    set(toUpdate);
  },
}));

export async function getIsTagsActive() {
  const dom = [
    ["name", "=", "product_tag"],
    ["state", "=", "activated"],
  ];
  const fields_names = ["state"];
  const { data, error } = await proxy.search("ir.module", dom, fields_names);
  const isTagsActive = data && data.length > 0 && data[0].state === "activated";
  const state = useStoreRest.getState();
  useStoreRest.setState({ ...state, isTagsActive });
  return isTagsActive;
}
